import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContentPageLayout } from '../components/Shared';
import {
  getPaymentGatewaySettings,
  updatePaymentGateway,
} from '../services/paymentGatewayApi';

type GatewayName = 'polar' | 'paypal';

const PaymentGatewayPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [gateway, setGateway] = useState<GatewayName>('polar');
  const [notes, setNotes] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await getPaymentGatewaySettings();
      setSettings(s.settings || null);
      const active = s.settings?.activeGateway as string | undefined;
      setGateway(active === 'paypal' ? 'paypal' : 'polar');
    } catch (e: any) {
      setError(e?.message || 'Failed to load gateway settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await updatePaymentGateway(gateway, notes);
      setSuccess(res.message || `Switched to ${gateway}`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update gateway');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <ContentPageLayout>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </ContentPageLayout>
    );
  }

  return (
    <ContentPageLayout>
      <Box>
        <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
          {t('sidebar.paymentGateway')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Primary MoR: <strong>Polar</strong>. PayPal is yearly-only interim.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2, maxWidth: 640 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Active: <strong>{(settings?.activeGateway as string) || '—'}</strong>
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="gateway-label">Active gateway</InputLabel>
            <Select
              labelId="gateway-label"
              label="Active gateway"
              value={gateway}
              onChange={(e) => setGateway(e.target.value as GatewayName)}
            >
              <MenuItem value="polar">Polar (MoR)</MenuItem>
              <MenuItem value="paypal">PayPal (yearly interim only)</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Paper>
      </Box>
    </ContentPageLayout>
  );
};

export default PaymentGatewayPage;
